"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

/**
 * Cuantos negocios estan abiertos ahora mismo, en verde y en vivo.
 *
 * Va en el navegador y no en la pagina porque las paginas del directorio estan
 * cacheadas una hora: un numero "de ahora mismo" dentro de una pagina guardada
 * diria la verdad una vez de cada sesenta. Asi la pagina sigue siendo rapida y
 * este trozo, que es el unico que cambia por minutos, se pide aparte.
 *
 * El numero es REAL: sale de los horarios de cada negocio, en hora de Mallorca.
 * Sube a la hora de comer, baja los lunes por la manana y se queda en nada de
 * madrugada. No se rellena ni se redondea al alza.
 */
export function ContadorAbiertos({
  lugarId,
  href,
  texto,
  activo = false,
  sobreOscuro = false,
}: {
  lugarId: string;
  /** A donde lleva al pulsarlo: el listado filtrado. */
  href: string;
  /**
   * La etiqueta ya traducida. Es una CADENA y no una funcion a proposito: esto
   * es un componente de cliente, y una funcion no cruza la frontera desde el
   * servidor —compila igual y revienta al pintar—.
   */
  texto: string;
  activo?: boolean;
  /** En la cabecera oscura de la guia. */
  sobreOscuro?: boolean;
}) {
  const [abiertos, setAbiertos] = useState<number | null>(null);

  useEffect(() => {
    let vivo = true;
    const mirar = async () => {
      try {
        const r = await fetch(`/api/directorio/abiertos?lugar=${lugarId}`);
        if (!r.ok) return;
        const cuerpo = await r.json();
        if (vivo && typeof cuerpo.abiertos === "number") setAbiertos(cuerpo.abiertos);
      } catch {
        // Si falla, no se ensena nada: mejor eso que un numero viejo.
      }
    };
    mirar();
    const reloj = setInterval(mirar, 120_000);
    return () => {
      vivo = false;
      clearInterval(reloj);
    };
  }, [lugarId]);

  // Mientras no se sabe, no ocupa sitio: nada de esqueletos que luego bailan.
  if (abiertos === null || abiertos === 0) return null;

  return (
    <Link
      href={href}
      className={
        sobreOscuro
          ? "inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm text-white backdrop-blur transition-colors hover:bg-white/20"
          : activo
          ? "inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800"
          : "inline-flex items-center gap-2 rounded-lg border border-zinc-200 px-3 py-1.5 text-sm text-zinc-700 transition-colors hover:border-emerald-300 hover:text-emerald-800"
      }
    >
      <span className="relative flex h-2 w-2" aria-hidden>
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      {texto} · {abiertos}
    </Link>
  );
}
